import { faMessagePlus } from '@fortawesome/pro-light-svg-icons';
import { faMessage, faComments } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { ReturnItem } from 'returnCenter-api';
import styled from 'styled-components';
import { LIGHT_BLUE } from 'styles/globalstyles';

const PREVIEW_LENGTH = 3;

type CommentIndicatorCellProps = {
    item: ReturnItem;
    onClick: () => void;
    disabled?: boolean;
};

/** Every comment on the row, labelled, one per line; empty when there are none. */
export const commentTooltip = (item: ReturnItem, t: TFunction) => {
    const parts: string[] = [];
    const customer = item.customerComment?.trim();
    const internal = item.comment?.trim();
    const external = item.externalComment?.trim();
    if (customer) parts.push(`${t('customerComment', 'Customer comment')}: ${customer}`);
    if (internal) parts.push(`${t('notes')}: ${internal}`);
    if (external) parts.push(`${t('externalComment')}: ${external}`);
    return parts.join('\n');
};

const CommentIndicatorCell = ({ item, onClick, disabled }: CommentIndicatorCellProps) => {
    const { t } = useTranslation();
    const internal = item.comment?.trim() ?? '';
    const external = item.externalComment?.trim() ?? '';
    const customer = item.customerComment?.trim() ?? '';
    const commentCount = [internal, external, customer].filter((c) => c.length > 0).length;
    const hasComment = commentCount > 0;
    const tooltip = commentTooltip(item, t);

    let preview: React.ReactNode;
    if (!hasComment) {
        preview = (
            <StyledFlex>
                <FontAwesomeIcon icon={faMessagePlus} color="textFaded" fontSize={12} />
                <StyledText>{t('add', 'Add')}</StyledText>
            </StyledFlex>
        );
    } else if (commentCount > 1) {
        preview = (
            <StyledFlex>
                <FontAwesomeIcon icon={faComments} color={LIGHT_BLUE} fontSize={12} />
                <StyledText $color={LIGHT_BLUE}> {t('multipleComments')}</StyledText>
            </StyledFlex>
        );
    } else {
        const source = customer || internal || external;
        preview = (
            <StyledFlex>
                <FontAwesomeIcon icon={faMessage} color={LIGHT_BLUE} fontSize={12} />
                <StyledText $color={LIGHT_BLUE}>
                    {source.substring(0, PREVIEW_LENGTH) + (source.length > PREVIEW_LENGTH ? '...' : '')}
                </StyledText>
            </StyledFlex>
        );
    }

    return (
        <PreviewButton
            type="button"
            disabled={disabled}
            $active={hasComment}
            title={tooltip || undefined}
            aria-label={t('notes')}
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onClick();
            }}
        >
            {preview}
        </PreviewButton>
    );
};

export default CommentIndicatorCell;

const PreviewButton = styled.button<{ $active: boolean }>`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0.25rem 0.375rem;
    display: inline-flex;
    align-items: center;
    font-family: inherit;
    font-size: 12px;
    white-space: nowrap;
    border-radius: var(--radius);
    color: ${({ $active, theme }) => ($active ? LIGHT_BLUE : theme.colors.textLight)};
    transition: color 0.15s ease, background-color 0.15s ease;

    &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme.colors.fillLight};
        color: ${({ theme }) => theme.colors.main};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.4;
    }
`;

const StyledText = styled(Text)<{ $color?: string }>`
    font-family: inherit;
    font-size: 13px;
    margin: 0.5rem;
    color: ${({ $color, theme }) => $color || theme.colors.textLight};
`;

const StyledFlex = styled(Flex)`
    align-items: center;
    gap: 1px;
`;
